import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/auth-context';
import { ConfirmButton } from '../components/ConfirmButton';
import { useCancelBooking } from '../hooks/use-bookings';
import {
  useCancelFlight,
  useFlight,
  useRequestBooking,
} from '../hooks/use-flights';
import { ApiError } from '../lib/api-client';
import {
  formatDate,
  formatDateRange,
  formatDateTime,
  formatRoute,
} from '../lib/format';

export function FlightDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const flight = useFlight(id);
  const [message, setMessage] = useState('');
  const requestBooking = useRequestBooking(id ?? '');
  const cancelFlight = useCancelFlight(id ?? '');
  const cancelBooking = useCancelBooking();

  if (flight.isLoading) {
    return <p className="px-4 py-8 text-sm text-slate-400">Lädt …</p>;
  }
  if (flight.error || !flight.data) {
    return (
      <div className="px-4 py-8">
        <p className="text-sm text-rose-400">Flug nicht gefunden.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-xs text-brand-400"
        >
          ← zurück
        </button>
      </div>
    );
  }

  const o = flight.data;
  const isPilot = !!user && user.id === o.pilotUserId;
  const reg =
    o.aircraft?.registration ?? o.aircraftRegistrationText ?? 'unbekannt';
  const typeName = o.aircraft?.type?.name ?? o.aircraftType?.name ?? null;
  const seatsLeft = o.freeSeats - o.confirmedSeats;
  const my = o.myBookingRequest;
  const canRequest =
    !isPilot &&
    o.status === 'ACTIVE' &&
    seatsLeft > 0 &&
    (!my || my.status === 'CANCELLED' || my.status === 'REJECTED');
  const canCancelMyBooking =
    my && (my.status === 'REQUESTED' || my.status === 'WAITLISTED' || my.status === 'CONFIRMED');
  const canCancelFlight =
    isPilot && (o.status === 'ACTIVE' || o.status === 'FULL');

  const requestErr =
    requestBooking.error instanceof ApiError
      ? requestBooking.error.message
      : null;
  const flightCancelErr =
    cancelFlight.error instanceof ApiError ? cancelFlight.error.message : null;

  return (
    <div className="px-4 py-4 space-y-4">
      <button
        onClick={() => navigate(-1)}
        className="text-xs text-slate-400 hover:text-slate-200"
      >
        ← zurück
      </button>

      <header className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
        <p className="text-xs uppercase tracking-wide text-slate-500">
          {formatDateRange(o.startAt, o.endAt)}
        </p>
        <h1 className="mt-1 text-2xl font-semibold">
          {formatRoute(o.fromLocation, o.toLocation, o.roundTrip)}
        </h1>
        <p className="mt-2 text-sm text-slate-300">
          {reg}
          {typeName ? ` · ${typeName}` : ''}
        </p>
        {o.pilot && (
          <p className="mt-1 text-sm text-slate-400">
            Pilot: {o.pilot.firstName} {o.pilot.lastName}
          </p>
        )}
        <p className="mt-2 text-sm">
          {seatsLeft > 0
            ? `${seatsLeft} von ${o.freeSeats} Plätzen frei`
            : 'Alle Plätze belegt'}
          {o.sharedPic && (
            <span className="ml-2 rounded-full border border-brand-800 bg-brand-900/30 px-2 py-0.5 text-[10px] font-medium text-brand-200">
              Shared PiC
            </span>
          )}
          {o.favoritesOnly && (
            <span
              className="ml-2 rounded-full border border-amber-800 bg-amber-900/30 px-2 py-0.5 text-[10px] font-medium text-amber-200"
              title="Nur Favoriten des Piloten"
            >
              ★ Nur Favoriten
            </span>
          )}
        </p>
        {o.costShareText && (
          <p className="mt-1 text-sm text-slate-400">
            Kostenanteil: {o.costShareText}
          </p>
        )}
        {o.comment && (
          <p className="mt-3 rounded-lg bg-slate-950 p-3 text-sm text-slate-300 whitespace-pre-wrap">
            {o.comment}
          </p>
        )}
        {(o.adminCancelReason || o.pilotCancelReason) && (
          <p className="mt-3 rounded-lg border border-rose-900 bg-rose-950/40 p-3 text-sm text-rose-200">
            Storniert: {o.adminCancelReason ?? o.pilotCancelReason}
          </p>
        )}
        {o.event && (
          <Link
            to={`/app/events/${o.event.id}`}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-brand-800 bg-brand-900/30 px-3 py-2 text-xs text-brand-100 hover:bg-brand-900/50"
          >
            <span>📅</span>
            <span className="font-medium">{o.event.name}</span>
            <span className="text-brand-300">
              ·{' '}
              {o.event.endAt
                ? `${formatDate(o.event.startAt)} – ${formatDate(o.event.endAt)}`
                : formatDateTime(o.event.startAt)}
            </span>
          </Link>
        )}
      </header>

      {my && (
        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">
            Dein Status
          </p>
          <p className="mt-1 text-sm font-medium">{my.status}</p>
          {my.message && (
            <p className="mt-2 text-xs text-slate-400">
              Deine Nachricht: „{my.message}"
            </p>
          )}
          {my.decisionReason && (
            <p className="mt-2 text-xs text-slate-400">
              Begründung: {my.decisionReason}
            </p>
          )}
          {canCancelMyBooking && (
            <div className="mt-3">
              <ConfirmButton
                label={cancelBooking.isPending ? 'Wird storniert …' : 'Anfrage stornieren'}
                confirmLabel="Wirklich stornieren?"
                variant="neutral"
                disabled={cancelBooking.isPending}
                onConfirm={() => cancelBooking.mutate(my.id)}
                className="w-full"
              />
            </div>
          )}
        </section>
      )}

      {canRequest && (
        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-3">
          <h2 className="text-sm font-semibold">Platz anfragen</h2>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            placeholder="Nachricht an Piloten (optional)"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          />
          <button
            onClick={() => requestBooking.mutate(message.trim() || null)}
            disabled={requestBooking.isPending}
            className="w-full rounded-lg bg-brand-600 px-4 py-3 font-medium text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {requestBooking.isPending ? 'Sende Anfrage …' : 'Platz anfragen'}
          </button>
          {requestErr && (
            <p className="text-xs text-rose-400">{requestErr}</p>
          )}
        </section>
      )}

      {isPilot && o.status === 'ACTIVE' && (
        <button
          onClick={() => navigate(`/app/flights/${o.id}/edit`)}
          className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm font-medium hover:bg-slate-700"
        >
          ✏️ Flug bearbeiten
        </button>
      )}

      {canCancelFlight && (
        <section className="rounded-xl border border-rose-900 bg-rose-950/30 p-4">
          <h2 className="text-sm font-semibold text-rose-200">Flug stornieren</h2>
          <p className="mt-1 text-xs text-rose-300/80">
            Anfragende werden per E-Mail/Push informiert.
          </p>
          <div className="mt-3">
            <ConfirmButton
              label={cancelFlight.isPending ? 'Wird storniert …' : 'Flug stornieren'}
              withReason
              reasonPlaceholder="Grund (optional)"
              confirmLabel="Stornieren bestätigen"
              disabled={cancelFlight.isPending}
              onConfirm={(reason) => cancelFlight.mutate(reason)}
              className="w-full"
              error={flightCancelErr}
            />
          </div>
        </section>
      )}
    </div>
  );
}
